"use client";

import { useEffect, useState } from "react";
import { API_BASE } from "./api";
import { useAuth } from "./auth";

export interface CharacterDTO {
  id: number;
  name: string;
  realmSlug: string;
  realmName: string;
  region: string;
  classID: number;
  raceID?: number | null;
  level: number;
  faction: "HORDE" | "ALLIANCE" | string;
}

interface CharactersState {
  loading: boolean;
  list: CharacterDTO[];
  error: string | null;
}

const CACHE_KEY = "bonusroll:characters:v1";
const FRESH_MS = 30 * 60 * 1000; // 30 minutes — characters change rarely

interface PersistedShape {
  list: CharacterDTO[];
  fetchedAt: number;
}

function readPersisted(): PersistedShape | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedShape;
    if (!Array.isArray(parsed?.list) || !parsed.fetchedAt) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writePersisted(list: CharacterDTO[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ list, fetchedAt: Date.now() }),
    );
  } catch { /* quota — ignore */ }
}

/** Hook: returns the user's level-90+ characters from /api/characters.
 *
 *  Anonymous users get an empty list. Sign-in triggers a fetch (cached
 *  in localStorage for 30 min so we don't hammer the backend on every
 *  page nav). */
export function useCharacters(): CharactersState {
  const auth = useAuth();
  const initial = readPersisted();
  const [state, setState] = useState<CharactersState>({
    loading: false,
    list: initial?.list ?? [],
    error: null,
  });

  useEffect(() => {
    if (auth.state !== "signed-in") {
      setState({ loading: false, list: [], error: null });
      return;
    }
    const persisted = readPersisted();
    if (persisted && Date.now() - persisted.fetchedAt < FRESH_MS) {
      setState({ loading: false, list: persisted.list, error: null });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetch(`${API_BASE}/api/characters`, { credentials: "include" })
      .then(async (r) => {
        if (cancelled) return;
        if (!r.ok) {
          setState({ loading: false, list: [], error: `HTTP ${r.status}` });
          return;
        }
        const list = (await r.json()) as CharacterDTO[];
        writePersisted(list);
        setState({ loading: false, list, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ loading: false, list: [], error: String(err?.message ?? err) });
      });
    return () => { cancelled = true; };
  }, [auth.state]);

  return state;
}

/** Clear the cached character list — used by Settings → "Refresh roster". */
export function invalidateCharacters() {
  if (typeof window !== "undefined") {
    try { window.localStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
  }
}
